<p-treeTable
  [value]="treeData"
  [scrollable]="config.scrollable"
  [scrollHeight]="config.scrollHeight"
  [selectionMode]="config.enableCheckboxSelection ? 'checkbox' : $any(undefined)"
  [(selection)]="selectedNodes"
  (selectionChange)="onSelectionChange($event)"
  [rowHover]="true"
  [tableStyle]="{ 'min-width': getTableMinWidth() }"
  [lazy]="lazy"
  [lazyLoadOnInit]="false"
  [paginator]="paginator"
  [rows]="rows"
  [first]="first"
  [totalRecords]="totalRecords"
  [rowsPerPageOptions]="rowsPerPageOptions"
  [showCurrentPageReport]="paginator"
  currentPageReportTemplate="Showing {first} to {last} of {totalRecords} rolls"
  (onLazyLoad)="lazyLoad.emit($event)"
>
  <ng-template pTemplate="header">
    <tr>
      <!-- Widths live inline on the th (TBL-04): the name column takes whatever is left so a long
           Production Card number stays readable, every other column is capped. -->
      <th class="whitespace-nowrap" style="width: 22rem; min-width: 22rem">{{ 'Dispatch_Production_Card_Roll' | translateWF: 'Production Card / Roll' }}</th>
      @if (config.showColor) {
        <th style="width: 10rem">{{ 'Dispatch_Color' | translateWF: 'Color' }}</th>
      }
      @if (config.showQuality) {
        <th class="text-center" style="width: 6rem">{{ 'Dispatch_Quality' | translateWF: 'Quality' }}</th>
      }
      @if (config.showFabric) {
        <th style="width: 16rem">{{ 'Dispatch_Fabric' | translateWF: 'Fabric' }}</th>
      }
      @if (config.showType) {
        <th style="width: 10rem">{{ 'Dispatch_Type' | translateWF: 'Type' }}</th>
      }
      @if (config.showWeightLbs) {
        <th class="text-right" style="width: 9rem">{{ 'Dispatch_Weight_Lbs' | translateWF: 'Weight (lbs)' }}</th>
      }
      @if (config.showWeightKg) {
        <th class="text-right" style="width: 9rem">{{ 'Dispatch_Weight_Kg' | translateWF: 'Weight (kg)' }}</th>
      }
      @if (config.showWeightYds) {
        <th class="text-right" style="width: 9rem">{{ 'Dispatch_Weight_Yds' | translateWF: 'Weight (yds)' }}</th>
      }
      @if (config.showScannedAt) {
        <th style="width: 12rem">{{ 'Dispatch_Scanned_At' | translateWF: 'Scanned At' }}</th>
      }
      @if (config.showScannedBy) {
        <th style="width: 10rem">{{ 'Dispatch_Scanned_By' | translateWF: 'Scanned By' }}</th>
      }
      @if (config.showActions) {
        <th class="text-right" style="width: 7rem">{{ 'Common_Actions' | translateWF: 'Actions' }}</th>
      }
    </tr>
  </ng-template>

  <ng-template pTemplate="body" let-rowNode let-rowData="rowData">
    @if (config.enableCheckboxSelection) {
      <!-- Checkbox selection mode (Available Rolls) -->
      <tr [ttSelectableRow]="rowNode">
        <td>
          <!-- Single flex row so the checkbox, the toggler and the label share a baseline; the label
               truncates instead of wrapping onto a second line. -->
          <div class="row-sm-start-center min-w-0">
            <p-treeTableCheckbox [value]="rowNode" />
            <p-treeTableToggler [rowNode]="rowNode" />
            @if (rowData.type === 'production-card') {
              <i class="pi pi-box text-orange-500 shrink-0"></i>
              <span class="font-semibold truncate" [pTooltip]="getPcLabel(rowData)">{{ getPcLabel(rowData) }}</span>
              <p-tag [value]="(rowNode.node?.children?.length || 0) + ' rolls'" severity="secondary" class="shrink-0" />
            } @else {
              <span class="truncate" [pTooltip]="rowData.rollNumber">{{ rowData.rollNumber }}</span>
            }
          </div>
        </td>
        <ng-container *ngTemplateOutlet="sharedColumns; context: { $implicit: rowData }" />
      </tr>
    } @else {
      <!-- Non-selection mode (Scanned Rolls) -->
      <tr [ttRow]="rowNode" [class.font-semibold]="rowData.type === 'production-card'">
        <td>
          <div class="row-sm-start-center min-w-0">
            <p-treeTableToggler [rowNode]="rowNode" />
            @if (rowData.type === 'production-card') {
              <i class="pi pi-box text-orange-500 shrink-0"></i>
              <span class="text-sm truncate" [pTooltip]="getPcLabel(rowData)">{{ getPcLabel(rowData) }}</span>
              <span class="text-sm text-gray-500 shrink-0">({{ rowData.rollCount }} {{ 'Dispatch_Rolls' | translateWF: 'rolls' }})</span>
            } @else {
              <span class="font-mono truncate" [pTooltip]="rowData.rollNumber">{{ rowData.rollNumber }}</span>
              @if (rowData.scannedAt) {
                <i class="pi pi-check-circle text-green-500 shrink-0"></i>
              }
            }
          </div>
        </td>
        <ng-container *ngTemplateOutlet="sharedColumns; context: { $implicit: rowData }" />
      </tr>
    }
  </ng-template>

  <ng-template pTemplate="emptymessage">
    <tr>
      <td [attr.colspan]="getColumnCount()" class="text-center py-4">
        {{ config.emptyMessage }}
      </td>
    </tr>
  </ng-template>
</p-treeTable>

<!-- Shared columns template to avoid duplication -->
<ng-template #sharedColumns let-rowData>
  <!-- Color -->
  @if (config.showColor) {
    <td>
      {{ getColorName(rowData.colorReference ?? '') }}
    </td>
  }

  <!-- Quality -->
  @if (config.showQuality) {
    <td class="text-center">
      @if (rowData.type === 'roll') {
        @if (getRollQuality(rowData) === 0) {
          <i class="pi pi-check-circle text-green-500 text-xl" [pTooltip]="'Dispatch_First_Quality' | translateWF: 'First Quality'"></i>
        } @else {
          <i class="pi pi-exclamation-triangle text-orange-500 text-xl" [pTooltip]="'Dispatch_BGrade' | translateWF: 'BGrade'"></i>
        }
      }
    </td>
  }

  <!-- Fabric (Available Rolls only) -->
  @if (config.showFabric) {
    <td>
      @if (rowData.type === 'roll') {
        ({{ rowData.fabricCode }}) {{ getFabricByCode(rowData.fabricCode)?.longDescription }}
      }
    </td>
  }

  <!-- Type (Available Rolls only) -->
  @if (config.showType) {
    <td>
      @if (rowData.type === 'roll') {
        {{ fabricFormValues[getFabricByCode(rowData.fabricCode)?.fabricForm ?? 0] }}
      }
    </td>
  }

  <!-- Weight (lbs) -->
  @if (config.showWeightLbs) {
    <td class="text-right tabular-nums">
      @if (rowData.type === 'production-card') {
        {{ rowData.totalWeight | number: '1.2-2' }}
      } @else if (rowData.type === 'roll') {
        {{ getWeightLbs(rowData) | number: '1.2-2' }}
      }
    </td>
  }

  <!-- Weight (kg) -->
  @if (config.showWeightKg) {
    <td class="text-right tabular-nums">
      @if (rowData.type === 'production-card') {
        <!-- Only the scanned-rolls tree carries a group total; without it this rendered a stray 0.00. -->
        @if ((rowData.totalWeight ?? 0) > 0) {
          {{ lbsToKg(rowData.totalWeight) | number: '1.2-2' }}
        }
      } @else if (rowData.type === 'roll') {
        {{ getWeightKg(rowData) | number: '1.2-2' }}
      }
    </td>
  }

  <!-- Weight (yds) -->
  @if (config.showWeightYds) {
    <td class="text-right tabular-nums">
      @if (rowData.type === 'production-card') {
        @if ((rowData.totalYards ?? 0) > 0) {
          {{ rowData.totalYards | number: '1.2-2' }}
        }
      } @else if (rowData.type === 'roll') {
        @if ((rowData.yards ?? 0) > 0) {
          {{ rowData.yards | number: '1.2-2' }}
        }
      }
    </td>
  }

  <!-- Scanned At (Scanned Rolls only) -->
  @if (config.showScannedAt) {
    <td>
      @if (rowData.type === 'roll') {
        @if (rowData.scannedAt) {
          {{ rowData.scannedAt | date: 'short' }}
        } @else {
          <span class="text-gray-400">-</span>
        }
      }
    </td>
  }

  <!-- Scanned By (Scanned Rolls only) -->
  @if (config.showScannedBy) {
    <td>
      @if (rowData.type === 'roll') {
        @if (rowData.scannedBy) {
          {{ rowData.scannedBy }}
        } @else {
          <span class="text-gray-400">-</span>
        }
      }
    </td>
  }

  <!-- Actions (Scanned Rolls only) -->
  @if (config.showActions) {
    <td class="text-right">
      @if (rowData.type === 'roll') {
        <p-button icon="pi pi-trash" variant="text" severity="danger" (onClick)="onDeleteRoll(rowData.rollNumber)" [pTooltip]="'Common_Delete' | translateWF: 'Delete'" />
      }
    </td>
  }
</ng-template>
